<template>
  <ButtonSquare
    :title="$t('general__view__model')"
    :imgSource="iconModel"
    size="large"
    :activeState="activeState"
    :hoverButtons="hoverButtons"
    :activeHoverButtonValue="currentModel"
    :showLoadingSpinner="isLoadingModel"
    @clicked="onClick"
    @onHoverButtonClick="changeModel"
    @mouseenter="onHover"
  />
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import ButtonSquare from '@/components/general/buttons/ButtonSquare/ButtonSquare.vue'
import iconModel from '@/assets/icon-model.svg'
import { SET_CURRENT_MODEL } from '@/store/mutation-types'
import { MAPVIEW_PREFIX, PLANVIEW_PREFIX } from '@/store/module-prefixes'
import { setViewToModelview, setViewToPlanview } from '@/utils/views'
import { zoomToEntities } from '@/composables/useMapbox'

// Setup store and emits
const store = useStore()
const emit = defineEmits(['ModelButtonHovered'])

// Computed properties for state and getters
const selectedLevel = computed(() => store.state.selectedLevel)
const selectedBuilding = computed(() => store.state[MAPVIEW_PREFIX].selectedBuilding)
const currentModel = computed(() => store.state[PLANVIEW_PREFIX].currentModel)
const isLoadingModel = computed(() => store.state[PLANVIEW_PREFIX].isLoadingModel)
const isDrawPointActive = computed(() => store.getters.isDrawPointActive)
const isModelviewShowing = computed(() => store.getters.isModelviewShowing)

// Build hoverButtons array
const hoverButtons = computed(() => {
  const buttons: { title: string; emit: string }[] = []
  if (!selectedLevel.value?.models3d?.length) return buttons
  selectedLevel.value.models3d.forEach((el: any, index: number) => {
    buttons.push({
      title: el.title
        ? el.title
        : el.model3dType.charAt(0).toUpperCase() +
          el.model3dType.replaceAll('_', ' ').slice(1).toLowerCase(),
      emit: `${el.model3dType}_MODEL_${index}`
    })
  })
  // Make ARCHITECTURAL_MODEL appear first if available
  const idx = buttons.findIndex((el, i) => el.emit === `ARCHITECTURAL_MODEL_${i}`)
  if (idx !== -1) {
    const obj = buttons.splice(idx, 1)
    buttons.push(obj[0])
  }
  return buttons
})

// Compute activeState
const activeState = computed(() => {
  if (hoverButtons.value && hoverButtons.value.length && !isDrawPointActive.value) {
    return isModelviewShowing.value ? 'active' : 'inactive'
  }
  return 'deactivated'
})

// Watch hoverButtons and update currentModel accordingly
watch(
  hoverButtons,
  (newVal) => {
    if (newVal.length) {
      setCurrentModel({ type: newVal[newVal.length - 1].emit })
    } else {
      setCurrentModel({ type: null })
    }
  },
  { immediate: true }
)

// Mutation helper
function setCurrentModel(payload: { type: string | null }) {
  store.commit(`${PLANVIEW_PREFIX}/${SET_CURRENT_MODEL}`, payload)
}

// Methods
async function onClick() {
  if (activeState.value === 'deactivated') return null
  // Switch view
  if (!isModelviewShowing.value) setViewToModelview()
  else setViewToPlanview()
  // Zoom to building
  const featureObject = {
    type: 'Feature',
    id: selectedBuilding.value.id,
    geometry: selectedBuilding.value.geometry,
    properties: {
      ...selectedBuilding.value
    }
  }
  zoomToEntities([featureObject.id])
}

function changeModel(value: string) {
  setCurrentModel({ type: value })
  if (!isModelviewShowing.value) onClick()
}

function onHover() {
  if (activeState.value !== 'deactivated') {
    emit('ModelButtonHovered')
  }
}
</script>
